/* kr-press-engine.js — KR Maps Press: the book-drafting engine.
 *
 * A faithful JS port of the KR Maps Journeys app's book pipeline
 * (design/lib: away_period_detector, place_cluster_detector, book_builder,
 * book_engine, book_enrich) PLUS a studio-shape adapter that turns ingested
 * PhotoMeta records into the shapes the Press Studio consumes:
 * StudioPhoto records, CHAPTERS-shaped legs, TRIP_WPS waypoints, reveal data
 * and Open-Meteo enrichment.
 *
 * Pure data-in / data-out. No DOM, no KRPress dependency — loads in the
 * browser (window.KRPressEngine) and under Node (module.exports) for tests.
 * Deterministic: no Math.random, no Date.now in any composition path.
 */
(function () {
  'use strict';

  // ───────────────────────── constants ─────────────────────────

  var CONST = {
    // away_period_detector.dart
    HOME_RADIUS_KM: 8,
    MIN_AWAY_HOURS: 6,
    MIN_HOME_RETURN_HOURS: 18,

    // place_cluster_detector.dart
    DBSCAN_EPS_METERS: 40000,
    DBSCAN_MIN_PHOTOS: 8,          // minPts, neighbours EXCLUDING self
    VISIT_GAP_DAYS: 60,
    BBOX_PREFILTER_LAT_DEG: 0.5,
    BBOX_PREFILTER_LNG_DEG: 0.7,
    PATTERN_RECURRING_MIN_VISITS: 4,
    PATTERN_RECURRING_MIN_SPAN_DAYS: 365,
    PATTERN_SUSTAINED_MIN_SPAN_DAYS: 180,

    // book_builder.dart
    WAYPOINT_MERGE_KM: 1.2,
    SCORE_BASE: 0.6,
    SCORE_LAND_BONUS: 0.08,
    SCORE_MP_MAX_BONUS: 0.12,
    SCORE_MP_CAP: 16,
    SCORE_CLAMP: [0.5, 0.9],

    // book_engine.dart
    AES_W_SCORE: 0.5,
    AES_W_SHARP: 0.25,
    AES_GOLDEN_BONUS: 0.12,
    AES_SCENIC_BONUS: 0.10,
    AES_FACES_BONUS: 0.05,
    GOLDEN_BEFORE_MIN: 430,        // ≤ 07:10 local
    GOLDEN_AFTER_MIN: 1050,        // ≥ 17:30 local
    SHARP_CALLOUT: 0.85,
    SCENIC_LABELS: ['sea', 'sunset', 'mountain', 'temple', 'cliff', 'landscape',
      'palace', 'volcano', 'rice terrace', 'garden', 'reflection', 'water'],
    MAX_BOOK_SPREADS: 50,
    PHOTOS_PER_PAGE_DEFAULT: 6,
    FRAMES_PER_LEG: [[2, 10], [4, 6], [8, 4], [Infinity, 3]],
    FRAMES_CAP_DAYS: [[4, 8], [8, 5], [16, 3], [28, 2], [Infinity, 1]],
    LEG_HERO_LAND_BONUS: 0.1,

    // book_enrich.dart
    ENRICH_TIMEOUT_MS: 6000,
    ELEVATION_URL: 'https://api.open-meteo.com/v1/elevation',
    ARCHIVE_URL: 'https://archive-api.open-meteo.com/v1/archive',

    EARTH_RADIUS_KM: 6371,

    // studio adapter (spec-mobile.md §5 Photo record + leg detection)
    STUDIO_SCORE_GOLDEN_BONUS: 0.07,
    STUDIO_SCORE_MAX: 0.92,
    LEG_SPLIT_KM: 120,             // day-dominant clusters farther apart ⇒ new leg
    LEG_SPLIT_GAP_DAYS: 60,        // same place, but this long since the last frame ⇒ new leg too (a separate visit, not one trip with a gap in the middle — matches VISIT_GAP_DAYS' definition of "distinct visit" used elsewhere for pattern detection)
    LEG_GAP_HOURS: 6,              // mid-day gap that can host a leg boundary
    WAYPOINT_CAP: 24               // ~cap; merge distance rises until under it
  };

  var TERRACOTTA = ['#C8432F', '#C97A3B', '#A65B2E', '#8A4A28', '#6F3D24'];

  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var MONTH = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  var DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
    'Friday', 'Saturday'];

  var MS_HOUR = 3600000;
  var MS_DAY = 86400000;

  // ───────────────────────── shared helpers ─────────────────────────

  /** Haversine great-circle distance in km (the builder's _hav, verbatim). */
  function haversineKm(aLat, aLng, bLat, bLng) {
    var rad = function (x) { return x * Math.PI / 180; };
    var dLa = rad(bLat - aLat), dLo = rad(bLng - aLng);
    var h = Math.pow(Math.sin(dLa / 2), 2) +
      Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.pow(Math.sin(dLo / 2), 2);
    return 2 * CONST.EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  function clamp(x, lo, hi) { return Math.min(hi, Math.max(lo, x)); }
  function pad2(n) { return String(n).padStart(2, '0'); }
  function floorDays(ms) { return Math.floor(ms / MS_DAY); }
  function timeMs(t) { return t instanceof Date ? t.getTime() : +t; }

  // Tolerant field access — the Dart model says latitude/longitude, the JS
  // typedef says lat/lng; accept both so callers can't get it wrong.
  function pLat(p) { return p.lat != null ? p.lat : (p.latitude != null ? p.latitude : 0); }
  function pLng(p) { return p.lng != null ? p.lng : (p.longitude != null ? p.longitude : (p.lon != null ? p.lon : 0)); }
  function pId(p) { return p.photoId != null ? p.photoId : p.id; }

  function titleCase(s) {
    var t = String(s == null ? '' : s).trim();
    if (!t) return t;
    return t.split(/\s+/).map(function (w) {
      return w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w;
    }).join(' ');
  }

  function minutesOfDay(t) {
    if (t == null) return null;
    var d = t instanceof Date ? t : new Date(t);
    return d.getHours() * 60 + d.getMinutes();
  }

  /** Degrees+minutes with the 60′ carry fix: never emits 40°60′. */
  function dms(value, isLat) {
    var abs = Math.abs(value);
    var deg = Math.floor(abs);
    var min = Math.round((abs - deg) * 60);
    if (min === 60) { min = 0; deg += 1; }
    var hemi = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
    return pad2(deg) + '°' + pad2(min) + '′' + hemi; // 08°30′S
  }

  /** 'August 2018' or 'Aug 2018 – Sep 2018'. Fixed English tables. */
  function datesLong(start, end) {
    if (start == null && end == null) return '';
    if (start == null) start = end;
    if (end == null) end = start;
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      return MONTH[start.getMonth()] + ' ' + start.getFullYear();
    }
    return MON[start.getMonth()] + ' ' + start.getFullYear() +
      ' – ' + MON[end.getMonth()] + ' ' + end.getFullYear();
  }

  /** '3 Aug' | '3 – 4 Aug' | '3 Aug – 2 Sep'. */
  function datesCompact(start, end) {
    if (start == null && end == null) return '';
    if (start == null) start = end;
    if (end == null) end = start;
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      if (start.getDate() === end.getDate()) return start.getDate() + ' ' + MON[start.getMonth()];
      return start.getDate() + ' – ' + end.getDate() + ' ' + MON[end.getMonth()];
    }
    return start.getDate() + ' ' + MON[start.getMonth()] +
      ' – ' + end.getDate() + ' ' + MON[end.getMonth()];
  }

  /** 'EEEE, MMM d' → 'Friday, Aug 3'. */
  function fmtDayBodyDate(d) {
    return DOW[d.getDay()] + ', ' + MON[d.getMonth()] + ' ' + d.getDate();
  }

  /** WMO weather-code → short label (Open-Meteo daily weather_code). */
  function wmoLabel(c) {
    if (c == null) return null;
    if (c === 0) return 'Clear';
    if (c <= 2) return 'Partly cloudy';
    if (c === 3) return 'Overcast';
    if (c <= 48) return 'Fog';
    if (c <= 57) return 'Drizzle';
    if (c <= 67) return 'Rain';
    if (c <= 77) return 'Snow';
    if (c <= 82) return 'Showers';
    if (c <= 86) return 'Snow showers';
    return 'Thunderstorm'; // 95–99
  }

  /** Strictly-greatest count wins, first (insertion order) on ties. */
  function firstMaxKey(map) {
    var best = null, bestN = 0;
    map.forEach(function (n, k) { if (n > bestN) { bestN = n; best = k; } });
    return best;
  }

  function tiered(table, count) {
    for (var i = 0; i < table.length; i++) if (count <= table[i][0]) return table[i][1];
    return table[table.length - 1][1];
  }

  function firstWhere(arr, fn, orElse) {
    for (var i = 0; i < arr.length; i++) if (fn(arr[i])) return arr[i];
    return orElse;
  }

  // ─────────────────── stage 1 — library analysis ───────────────────

  /**
   * Faithful state machine from away_period_detector.dart.
   * detectAwayPeriods({photos, homes}) → AwayPeriod[]
   */
  function detectAwayPeriods(input) {
    var photos = (input && input.photos) || [];
    var homes = (input && input.homes) || [];
    var dated = photos.filter(function (p) { return p && p.takenAt != null; })
      .slice().sort(function (a, b) { return timeMs(a.takenAt) - timeMs(b.takenAt); });
    if (!dated.length) return [];

    var periods = [];
    var currentAway = [];
    var pendingReturn = [];

    function isAtAnyHome(p) {
      if (!homes.length) return false;
      if (p.ignoreLocation) return false; // user override — treat as away
      for (var i = 0; i < homes.length; i++) {
        var h = homes[i];
        var r = h.radiusKm != null ? h.radiusKm : CONST.HOME_RADIUS_KM;
        if (haversineKm(pLat(p), pLng(p), pLat(h), pLng(h)) <= r) return true;
      }
      return false;
    }

    function closePeriod() {
      // Trailing pending-return photos belong to the home period, not the trip.
      pendingReturn.length = 0;
      if (!currentAway.length) return;
      var span = timeMs(currentAway[currentAway.length - 1].takenAt) - timeMs(currentAway[0].takenAt);
      if (span >= CONST.MIN_AWAY_HOURS * MS_HOUR) {
        periods.push({
          start: currentAway[0].takenAt,
          end: currentAway[currentAway.length - 1].takenAt,
          photos: currentAway.slice()
        });
      }
      currentAway.length = 0;
    }

    for (var i = 0; i < dated.length; i++) {
      var p = dated[i];
      if (isAtAnyHome(p)) {
        if (!currentAway.length) continue; // home → home
        pendingReturn.push(p);
        // Continuous home stretch exceeded 18h → the trip is over (strictly before).
        if (timeMs(pendingReturn[0].takenAt) + CONST.MIN_HOME_RETURN_HOURS * MS_HOUR < timeMs(p.takenAt)) {
          closePeriod();
        }
      } else {
        if (!currentAway.length) {
          currentAway.push(p);
        } else {
          if (pendingReturn.length) {          // brief pop home mid-trip →
            currentAway.push.apply(currentAway, pendingReturn); // part of the trip
            pendingReturn.length = 0;
          }
          currentAway.push(p);
        }
      }
    }
    closePeriod();
    return periods; // already sorted by start
  }

  function periodForPhoto(photo, periods) {
    if (!photo || photo.takenAt == null) return null;
    var t = timeMs(photo.takenAt);
    for (var i = 0; i < (periods || []).length; i++) {
      var p = periods[i];
      if (t >= timeMs(p.start) && t <= timeMs(p.end)) return p; // inclusive both ends
    }
    return null;
  }

  /**
   * DBSCAN from place_cluster_detector.dart. Fixed minPts 8 (self excluded —
   * a tight group of exactly 8 photos does NOT cluster; you need ≥9).
   */
  function detectPlaceClusters(input) {
    var photos = (input || []).filter(function (p) {
      return p && (pLat(p) !== 0 || pLng(p) !== 0);
    });
    var n = photos.length;
    if (n < CONST.DBSCAN_MIN_PHOTOS) return [];

    var visited = new Array(n).fill(false);
    var clusterId = new Array(n).fill(-1);
    var nextCluster = 0;

    function regionQuery(i) {
      var neighbors = [];
      var aLat = pLat(photos[i]), aLng = pLng(photos[i]);
      for (var j = 0; j < n; j++) {
        if (i === j) continue;
        if (Math.abs(pLat(photos[j]) - aLat) > CONST.BBOX_PREFILTER_LAT_DEG) continue;
        if (Math.abs(pLng(photos[j]) - aLng) > CONST.BBOX_PREFILTER_LNG_DEG) continue;
        var d = haversineKm(aLat, aLng, pLat(photos[j]), pLng(photos[j])) * 1000;
        if (d <= CONST.DBSCAN_EPS_METERS) neighbors.push(j);
      }
      return neighbors;
    }

    for (var i = 0; i < n; i++) {
      if (visited[i]) continue;
      visited[i] = true;
      var neighbors = regionQuery(i);
      if (neighbors.length < CONST.DBSCAN_MIN_PHOTOS) continue; // not a core point
      var cid = nextCluster++;
      clusterId[i] = cid;
      var queue = neighbors.slice();
      while (queue.length) {
        var j = queue.pop(); // LIFO — matches Dart removeLast
        if (!visited[j]) {
          visited[j] = true;
          var nj = regionQuery(j);
          if (nj.length >= CONST.DBSCAN_MIN_PHOTOS) queue.push.apply(queue, nj);
        }
        if (clusterId[j] === -1) clusterId[j] = cid; // border points join, don't expand
      }
    }

    var byCluster = new Map();
    for (var k = 0; k < n; k++) {
      if (clusterId[k] >= 0) {
        if (!byCluster.has(clusterId[k])) byCluster.set(clusterId[k], []);
        byCluster.get(clusterId[k]).push(photos[k]);
      }
    }

    var result = [];
    byCluster.forEach(function (ps, cid2) {
      var latSum = 0, lngSum = 0;
      for (var m = 0; m < ps.length; m++) { latSum += pLat(ps[m]); lngSum += pLng(ps[m]); }
      var dates = ps.map(function (p) { return p.takenAt; })
        .filter(function (t) { return t != null; })
        .sort(function (a, b) { return timeMs(a) - timeMs(b); });
      if (!dates.length) return; // skip cluster entirely
      var visits = 1;
      for (var v = 1; v < dates.length; v++) {
        if (floorDays(timeMs(dates[v]) - timeMs(dates[v - 1])) > CONST.VISIT_GAP_DAYS) visits++;
      }
      var spanDays = floorDays(timeMs(dates[dates.length - 1]) - timeMs(dates[0]));
      var pattern = (visits >= CONST.PATTERN_RECURRING_MIN_VISITS &&
        spanDays >= CONST.PATTERN_RECURRING_MIN_SPAN_DAYS) ? 'recurring'
        : spanDays >= CONST.PATTERN_SUSTAINED_MIN_SPAN_DAYS ? 'sustained'
          : 'oneBurst';
      result.push({
        id: 'place_' + cid2,
        photos: ps,
        centerLat: latSum / ps.length,
        centerLng: lngSum / ps.length,
        firstDate: dates[0],
        lastDate: dates[dates.length - 1],
        pattern: pattern,
        visitCount: visits
      });
    });

    result.sort(function (a, b) { return b.photos.length - a.photos.length; });
    return result;
  }

  // ─────────────────── regions (trimmed regional_grouping_service) ───────────────────

  var _regions = { list: null, promise: null };

  function _walkCoords(coords, acc) {
    if (typeof coords[0] === 'number') { acc.lng += coords[0]; acc.lat += coords[1]; acc.n++; return; }
    for (var i = 0; i < coords.length; i++) _walkCoords(coords[i], acc);
  }

  function pointInPolygon(lat, lng, ring /* [[lng,lat],…] */) {
    var inside = false;
    for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      var xi = ring[i][1], yi = ring[i][0];
      var xj = ring[j][1], yj = ring[j][0];
      if (((yi > lng) !== (yj > lng)) &&
        (lat < (xj - xi) * (lng - yi) / (yj - yi) + xi)) inside = !inside;
    }
    return inside;
  }

  function loadCountries(url) {
    if (_regions.promise) return _regions.promise;
    _regions.promise = (function () {
      return fetchWithTimeout(url, 20000, null).then(function (r) {
        if (!r || r.status !== 200) throw new Error('bad status');
        return r.json();
      }).then(function (gj) {
        var feats = (gj && gj.features) || [];
        var list = [];
        for (var i = 0; i < feats.length; i++) {
          var f = feats[i];
          var props = f.properties || {};
          var geom = f.geometry;
          if (!geom || !geom.coordinates) continue;
          var polys = []; // outer rings only — silhouettes
          if (geom.type === 'Polygon') polys.push(geom.coordinates[0]);
          else if (geom.type === 'MultiPolygon') {
            for (var g = 0; g < geom.coordinates.length; g++) polys.push(geom.coordinates[g][0]);
          } else continue;
          var acc = { lat: 0, lng: 0, n: 0 };
          _walkCoords(geom.coordinates, acc); // centroid = average of ALL coords
          var minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
          for (var pI = 0; pI < polys.length; pI++) {
            var ring = polys[pI];
            for (var q = 0; q < ring.length; q++) {
              if (ring[q][1] < minLat) minLat = ring[q][1];
              if (ring[q][1] > maxLat) maxLat = ring[q][1];
              if (ring[q][0] < minLng) minLng = ring[q][0];
              if (ring[q][0] > maxLng) maxLng = ring[q][0];
            }
          }
          list.push({
            name: props.NAME || props.name || props.ADMIN || '',
            isoA2: props.ISO_A2 || props.iso_a2 || '',
            centroid: acc.n ? { lat: acc.lat / acc.n, lng: acc.lng / acc.n } : { lat: 0, lng: 0 },
            polygons: polys,
            bbox: [minLat, maxLat, minLng, maxLng]
          });
        }
        _regions.list = list;
        return list;
      }).catch(function () {
        _regions.list = []; // failure tolerated — lookups return null, book still renders
        return _regions.list;
      });
    })();
    return _regions.promise;
  }

  function _regionForPoint(lat, lng) {
    var list = _regions.list;
    if (!list) return null;
    for (var i = 0; i < list.length; i++) {
      var r = list[i];
      if (lat < r.bbox[0] || lat > r.bbox[1] || lng < r.bbox[2] || lng > r.bbox[3]) continue;
      for (var pI = 0; pI < r.polygons.length; pI++) {
        if (pointInPolygon(lat, lng, r.polygons[pI])) return r;
      }
    }
    return null;
  }

  function countryForPoint(lat, lng) {
    var r = _regionForPoint(lat, lng);
    return r ? r.name : null;
  }

  function countryCentroid(name) {
    var list = _regions.list;
    if (!list) return null;
    var low = String(name || '').toLowerCase();
    for (var i = 0; i < list.length; i++) {
      if (list[i].name.toLowerCase() === low) return list[i].centroid;
    }
    return null;
  }

  function visitedCountryPoints(pts) {
    var out = new Map(); // first-seen order
    if (!_regions.list) return out;
    for (var i = 0; i < (pts || []).length; i++) {
      var name = countryForPoint(pLat(pts[i]), pLng(pts[i]));
      if (name == null) continue;
      if (!out.has(name)) out.set(name, []);
      out.get(name).push({ lat: pLat(pts[i]), lng: pLng(pts[i]) });
    }
    return out;
  }

  function outlineFor(name) {
    var list = _regions.list;
    if (!list) return null;
    var low = String(name || '').toLowerCase();
    var r = null;
    for (var i = 0; i < list.length; i++) if (list[i].name.toLowerCase() === low) { r = list[i]; break; }
    if (!r) return null;
    var minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    for (var pI = 0; pI < r.polygons.length; pI++) {
      var ring = r.polygons[pI];
      for (var q = 0; q < ring.length; q++) {
        if (ring[q][1] < minLat) minLat = ring[q][1];
        if (ring[q][1] > maxLat) maxLat = ring[q][1];
        if (ring[q][0] < minLng) minLng = ring[q][0];
        if (ring[q][0] > maxLng) maxLng = ring[q][0];
      }
    }
    var wrapped = (maxLng - minLng) > 180;
    if (wrapped) {
      var lo = 360, hi = -360;
      for (var pJ = 0; pJ < r.polygons.length; pJ++) {
        var ring2 = r.polygons[pJ];
        for (var q2 = 0; q2 < ring2.length; q2++) {
          var l = ring2[q2][0] < 0 ? ring2[q2][0] + 360 : ring2[q2][0];
          if (l < lo) lo = l;
          if (l > hi) hi = l;
        }
      }
      minLng = lo; maxLng = hi;
    }
    return {
      name: r.name, isoA2: r.isoA2, polygons: r.polygons, centroid: r.centroid,
      minLat: minLat, maxLat: maxLat, minLng: minLng, maxLng: maxLng, wrapped: wrapped
    };
  }

  function groupPhotosByRegion(photos, mode) {
    // 'world' grouping only. NOTE: replicates the Dart quirk — every 'Unknown'
    // photo shares the FIRST unknown photo's coords as the group pin (putIfAbsent).
    var groups = new Map();
    for (var i = 0; i < (photos || []).length; i++) {
      var p = photos[i];
      if (pLat(p) === 0 && pLng(p) === 0) continue;
      var name = mode === 'world' || mode == null ? countryForPoint(pLat(p), pLng(p)) : null;
      var centroid = name != null ? countryCentroid(name) : null;
      if (name == null || centroid == null) {
        name = 'Unknown';
        centroid = { lat: pLat(p), lng: pLng(p) };
      }
      if (!groups.has(name)) groups.set(name, { regionName: name, centroid: centroid, photos: [] });
      groups.get(name).photos.push(p);
    }
    return groups;
  }

  // ─────────────────── stage 2 — journey → book input ───────────────────

  /** Greedy 1.2 km merge, endpoints pinned (book_builder._simplifyNodes). */
  function simplifyNodes(pts, mergeKm) {
    if (mergeKm == null) mergeKm = CONST.WAYPOINT_MERGE_KM;
    var norm = (pts || []).map(function (p) { return { lat: pLat(p), lng: pLng(p) }; });
    if (norm.length <= 2) return norm;
    var out = [norm[0]];
    for (var i = 1; i < norm.length - 1; i++) {
      if (haversineKm(out[out.length - 1].lat, out[out.length - 1].lng, norm[i].lat, norm[i].lng) < mergeKm) continue;
      out.push(norm[i]);
    }
    var last = norm[norm.length - 1];
    if (haversineKm(out[out.length - 1].lat, out[out.length - 1].lng, last.lat, last.lng) < mergeKm && out.length > 1) {
      out[out.length - 1] = last; // replace, so the route still ends at the end
    } else {
      out.push(last);
    }
    return out;
  }

  function routeKm(pts) {
    var total = 0;
    for (var i = 1; i < (pts || []).length; i++) {
      total += haversineKm(pLat(pts[i - 1]), pLng(pts[i - 1]), pLat(pts[i]), pLng(pts[i]));
    }
    return total;
  }

  /** Raw photo score: 0.6 base +0.08 landscape + up to +0.12 megapixels, 0.5–0.9. */
  function rawScore(p, land) {
    var s = CONST.SCORE_BASE + (land ? CONST.SCORE_LAND_BONUS : 0);
    var mp = ((p.width || 0) * (p.height || 0)) / 1e6;
    if (mp > 0) s += Math.min(mp, CONST.SCORE_MP_CAP) / CONST.SCORE_MP_CAP * CONST.SCORE_MP_MAX_BONUS;
    return clamp(s, CONST.SCORE_CLAMP[0], CONST.SCORE_CLAMP[1]);
  }

  function isRealPhoto(p) { return (p.width || 0) > 0 || (p.height || 0) > 0 || p.hash != null; }

  function _nearestPlace(node, src, names) {
    var best = null, bestD = Infinity;
    for (var i = 0; i < src.length; i++) {
      var p = src[i];
      if (p.ignoreLocation) continue; // GPS intentionally dismissed
      var d = haversineKm(node.lat, node.lng, pLat(p), pLng(p));
      if (d < bestD) { bestD = d; best = p; } // strict < — first-wins on ties
    }
    if (!best) return null;
    var c = String(best.caption == null ? '' : best.caption).trim();
    if (c) return titleCase(c);
    var n = names[pId(best)];
    return (n != null && String(n).length) ? titleCase(n) : null;
  }

  function _dayDistance(ids, src) {
    if (ids.length < 2) return '';
    var km = 0;
    for (var k = 1; k < ids.length; k++) {
      var a = src[ids[k - 1]], b = src[ids[k]];
      if (a.ignoreLocation || b.ignoreLocation) continue; // skip dismissed-GPS hops
      km += haversineKm(pLat(a), pLng(a), pLat(b), pLng(b));
    }
    return km < 1 ? '' : Math.round(km) + ' km';
  }

  function _dayTitle(ids, bookPhotos, fallback) {
    var counts = new Map();
    for (var i = 0; i < ids.length; i++) {
      var c = bookPhotos[ids[i]].caption;
      counts.set(c, (counts.get(c) || 0) + 1);
    }
    if (!counts.size) return titleCase(fallback);
    return titleCase(firstMaxKey(counts));
  }

  function _tripCenterLng(trip) { return trip.centerLng != null ? trip.centerLng : (trip.centerLon || 0); }

  function _tripDisplayName(trip) {
    if (trip.customName != null) return trip.customName;
    if (trip.locationName == null) {
      if (trip.country != null && trip.country !== '') return trip.country;
      var lat = trip.centerLat || 0, lng = _tripCenterLng(trip);
      return Math.abs(lat).toFixed(1) + '°' + (lat >= 0 ? 'N' : 'S') + ' ' +
        Math.abs(lng).toFixed(1) + '°' + (lng >= 0 ? 'E' : 'W');
    }
    var detail = trip.locationName;
    if (trip.state != null && trip.state !== '') detail += ', ' + trip.state;
    else if (trip.country != null && trip.country !== '') detail += ', ' + trip.country;
    return detail;
  }

  function _tripDaySpan(trip) {
    if (trip.startDate != null && trip.endDate != null) {
      return floorDays(timeMs(trip.endDate) - timeMs(trip.startDate)) + 1;
    }
    return 1;
  }

  /**
   * Port of BookBuilder.build — one journey leg → BookJourney.
   * buildJourney({trip, photos, pathPoints, placeNames})
   */
  function buildJourney(args) {
    var trip = (args && args.trip) || {};
    var photos = (args && args.photos) || [];
    var pathPoints = (args && (args.pathPoints || args.path)) || [];
    var placeNames = (args && args.placeNames) || {};

    var region = [];
    if (trip.state != null && trip.state !== '') region.push(trip.state);
    if (trip.country != null && trip.country !== '') region.push(trip.country);
    region = region.join(', ');
    var regionShort = (trip.locationName != null && trip.locationName !== '') ? trip.locationName
      : (trip.state != null && trip.state !== '') ? trip.state
        : (trip.country != null && trip.country !== '') ? trip.country
          : (trip.customName != null ? trip.customName : 'the trip');

    // Real, renderable photos with coordinates, chronological, undated LAST.
    var src = photos.filter(function (p) {
      return p && !p.isVideo && isRealPhoto(p) && !(pLat(p) === 0 && pLng(p) === 0);
    }).slice().sort(function (a, b) {
      if (a.takenAt == null && b.takenAt == null) return 0;
      if (a.takenAt == null) return 1;
      if (b.takenAt == null) return -1;
      return timeMs(a.takenAt) - timeMs(b.takenAt);
    });

    // Waypoints = simplified path nodes, each named from the nearest photo.
    var trail = pathPoints.length ? pathPoints
      : src.filter(function (p) { return !p.ignoreLocation; });
    var nodes = simplifyNodes(trail);
    var waypoints = nodes.map(function (n, i) {
      var name = _nearestPlace(n, src, placeNames);
      return { name: name != null ? name : 'Stop ' + (i + 1), lat: n.lat, lng: n.lng };
    });

    function nearestWp(lat, lng) {
      if (!waypoints.length) return -1;
      var best = 0, bestD = Infinity;
      for (var i = 0; i < waypoints.length; i++) {
        var d = haversineKm(lat, lng, waypoints[i].lat, waypoints[i].lng);
        if (d < bestD) { bestD = d; best = i; } // strict <
      }
      return best;
    }

    function place(p) {
      var c = String(p.caption == null ? '' : p.caption).trim();
      if (c) return c.toUpperCase();
      var n = placeNames[pId(p)];
      if (n != null && String(n).length) return String(n).toUpperCase();
      return String(regionShort).toUpperCase();
    }

    var bookPhotos = [];
    for (var i = 0; i < src.length; i++) {
      var p = src[i];
      var land = ((p.width || 0) === 0 && (p.height || 0) === 0) ? true : (p.width || 0) >= (p.height || 0);
      bookPhotos.push({
        index: i,
        photoId: pId(p),
        caption: place(p),
        orient: land ? 'land' : 'port',
        score: rawScore(p, land),
        t: minutesOfDay(p.takenAt),
        wp: p.ignoreLocation ? -1 : nearestWp(pLat(p), pLng(p)),
        sharp: 0.7, faces: 0, labels: []
      });
    }

    // Group dated photos by LOCAL calendar day (undated photos join no day).
    var byDay = new Map();      // 'y-m-d' (unpadded) → photo indices
    var dayDate = new Map();
    for (var k = 0; k < src.length; k++) {
      var t = src[k].takenAt;
      if (t == null) continue;
      var d = t instanceof Date ? t : new Date(t);
      var key = d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(k);
      if (!dayDate.has(key)) dayDate.set(key, new Date(d.getFullYear(), d.getMonth(), d.getDate()));
    }
    var dayKeys = Array.from(byDay.keys()).sort(function (a, b) {
      return dayDate.get(a).getTime() - dayDate.get(b).getTime();
    });

    var days = [];
    for (var di = 0; di < dayKeys.length; di++) {
      var ids = byDay.get(dayKeys[di]);
      var title = _dayTitle(ids, bookPhotos, regionShort);
      var way = [];
      var seen = new Set();
      for (var w = 0; w < ids.length; w++) {
        var wp = bookPhotos[ids[w]].wp;
        if (wp >= 0 && wp < waypoints.length && !seen.has(waypoints[wp])) {
          seen.add(waypoints[wp]);
          way.push(waypoints[wp]); // SHARED refs — identity matters downstream
        }
      }
      var date = dayDate.get(dayKeys[di]);
      var frames = ids.length === 1 ? 'A single frame' : ids.length + ' frames';
      days.push({
        label: pad2(di + 1),
        title: title,
        body: frames + ' from ' + title + '. ' + fmtDayBodyDate(date) + '.',
        photos: ids,
        dist: _dayDistance(ids, src),
        wx: '',
        elev: null,
        way: way,
        quote: null,
        moment: null,
        date: date
      });
    }

    // Deviation from Dart (_routeKm(pathPoints)): when no GPX path is given the
    // Dart code reports 0 km even though it routed the photo trail; the port
    // spec's test vectors expect the trail distance, which is also what the
    // narrative should honestly say. Use the same trail the waypoints used.
    var kmTotal = routeKm(trail);
    var times = src.map(function (p) { return p.takenAt; })
      .filter(function (t2) { return t2 != null; })
      .sort(function (a, b) { return timeMs(a) - timeMs(b); });
    var first = times.length ? times[0] : null;
    var last = times.length ? times[times.length - 1] : null;
    var span = days.length > 1 ? days.length + ' days' : 'a day';

    return {
      key: trip.id,
      title: trip.customName != null ? trip.customName
        : (trip.locationName != null ? trip.locationName : _tripDisplayName(trip)),
      region: region,
      kicker: 'A FIELD JOURNAL',
      datesLong: datesLong(first != null ? new Date(timeMs(first)) : (trip.startDate || null),
        last != null ? new Date(timeMs(last)) : (trip.endDate || null)),
      coords: dms(trip.centerLat || 0, true) + ' · ' + dms(_tripCenterLng(trip), false),
      km: String(Math.round(kmTotal)),
      stops: waypoints.length,
      dayCount: days.length ? days.length : _tripDaySpan(trip),
      peak: '',
      narrative: 'A journey through ' + regionShort + ' — ' + span + ' and ' +
        Math.round(kmTotal) + ' km, traced from the places that earned a photograph.',
      closingMeta: 'KR MAPS · ' + (last != null ? new Date(timeMs(last)).getFullYear()
        : (trip.endDate != null ? new Date(timeMs(trip.endDate)).getFullYear() : 2018)),
      photos: bookPhotos,
      waypoints: waypoints,
      chapters: [],
      days: days,
      legs: [],
      dayLeg: []
    };
  }

  /**
   * Port of BookBuilder.buildTrip — legs in CHAIN ORDER stitched into one
   * whole-trip BookJourney (chapters stay contiguous, side-trips included).
   */
  function buildTrip(legsData, opts) {
    var tripName = opts && opts.tripName != null ? opts.tripName : null;
    var region = (opts && opts.region) || '';
    var allPhotos = [], allWaypoints = [], allDays = [], dayLeg = [], legs = [];
    var photoOff = 0, wpOff = 0, dayNo = 0;
    var i, k;

    for (i = 0; i < (legsData || []).length; i++) {
      var lg = legsData[i];
      var lj = buildJourney({
        trip: lg.trip, photos: lg.photos,
        pathPoints: lg.path || lg.pathPoints || [],
        placeNames: lg.placeNames || {}
      });
      if (!lj.photos.length) continue;
      var legIndex = legs.length;
      var firstDay = allDays.length;

      for (k = 0; k < lj.photos.length; k++) {
        var p = lj.photos[k];
        allPhotos.push({
          index: photoOff + p.index,
          photoId: p.photoId,
          caption: p.caption,
          orient: p.orient,
          score: p.score,
          t: p.t,
          wp: p.wp < 0 ? -1 : wpOff + p.wp,
          sharp: p.sharp, faces: p.faces, labels: p.labels
        });
      }
      allWaypoints.push.apply(allWaypoints, lj.waypoints); // same instances

      for (k = 0; k < lj.days.length; k++) {
        var d = lj.days[k];
        dayNo++;
        allDays.push({
          label: pad2(dayNo),
          title: d.title,
          body: d.body,
          photos: d.photos.map(function (x) { return photoOff + x; }),
          dist: d.dist,
          wx: d.wx,
          elev: d.elev,
          way: d.way,
          quote: d.quote,
          moment: d.moment,
          date: d.date
        });
        dayLeg.push(legIndex);
      }

      var hero = null, bestS = -1;
      for (k = 0; k < lj.photos.length; k++) {
        var ph = lj.photos[k];
        var s = ph.score + (ph.orient === 'land' ? CONST.LEG_HERO_LAND_BONUS : 0);
        if (s > bestS) { bestS = s; hero = photoOff + ph.index; } // strict > — first wins
      }

      legs.push({
        title: lg.trip.locationName != null ? lg.trip.locationName
          : (lg.trip.customName != null ? lg.trip.customName : lj.title),
        region: region,
        dates: datesCompact(lg.trip.startDate != null ? new Date(timeMs(lg.trip.startDate)) : null,
          lg.trip.endDate != null ? new Date(timeMs(lg.trip.endDate)) : null),
        text: String(lg.trip.notes == null ? '' : lg.trip.notes).trim(),
        dayCount: lj.days.length,
        photoCount: lj.photos.length,
        firstDayIndex: firstDay,
        heroPhoto: hero,
        centerLat: lg.trip.centerLat || 0,
        centerLng: _tripCenterLng(lg.trip)
      });

      photoOff += lj.photos.length;
      wpOff += lj.waypoints.length;
    }

    if (!allPhotos.length || !legs.length) {
      return {
        key: 'trip', title: tripName != null ? tripName : 'The Trip', region: region,
        kicker: '', datesLong: '', coords: '', km: '0', stops: 0, dayCount: 0, peak: '',
        narrative: '', closingMeta: '', photos: [], waypoints: [], chapters: [],
        days: [], legs: [], dayLeg: []
      };
    }

    var km = routeKm(allWaypoints); // waypoint chain, not raw paths
    var times = [];
    for (i = 0; i < legsData.length; i++) {
      for (k = 0; k < (legsData[i].photos || []).length; k++) {
        var t = legsData[i].photos[k].takenAt;
        if (t != null) times.push(timeMs(t));
      }
    }
    times.sort(function (a, b) { return a - b; });
    var centerLat = 0, centerLng = 0;
    for (i = 0; i < legs.length; i++) { centerLat += legs[i].centerLat; centerLng += legs[i].centerLng; }
    centerLat /= legs.length; centerLng /= legs.length;

    return {
      key: 'trip_' + legsData[0].trip.id,
      title: tripName != null ? tripName : legs[0].title + ' → ' + legs[legs.length - 1].title,
      region: region,
      kicker: 'A FIELD JOURNAL',
      datesLong: datesLong(times.length ? new Date(times[0]) : null,
        times.length ? new Date(times[times.length - 1]) : null),
      coords: dms(centerLat, true) + ' · ' + dms(centerLng, false),
      km: String(Math.round(km)),
      stops: allWaypoints.length,
      dayCount: allDays.length,
      peak: '',
      narrative: 'A ' + allDays.length + '-day trip across ' + legs.length + ' journeys — ' +
        legs.map(function (l) { return l.title; }).join(', ') + '.',
      closingMeta: 'KR MAPS · ' + (times.length ? new Date(times[times.length - 1]).getFullYear() : 2018),
      photos: allPhotos,
      waypoints: allWaypoints,
      chapters: [],
      days: allDays,
      legs: legs,
      dayLeg: dayLeg
    };
  }

  // ─────────────────── stage 3 — composition (pure) ───────────────────

  /** Per-photo aesthetic score + human-readable "why" parts (BookEngine.aesthetic). */
  function aesthetic(p) {
    var t = p.t;
    var sharp = p.sharp == null ? 0.7 : p.sharp;
    var faces = p.faces || 0;
    var labels = p.labels || [];
    var golden = t != null && (t <= CONST.GOLDEN_BEFORE_MIN || t >= CONST.GOLDEN_AFTER_MIN);
    var scenic = labels.some(function (l) { return CONST.SCENIC_LABELS.indexOf(l) !== -1; });
    var parts = [];
    var s = CONST.AES_W_SCORE * p.score + CONST.AES_W_SHARP * sharp;
    if (sharp >= CONST.SHARP_CALLOUT) parts.push('sharp');
    if (golden) { s += CONST.AES_GOLDEN_BONUS; parts.push('golden hour'); }
    if (scenic) { s += CONST.AES_SCENIC_BONUS; parts.push('scenic'); }
    if (faces > 0) { s += CONST.AES_FACES_BONUS; parts.push(faces + ' ' + (faces > 1 ? 'faces' : 'face')); }
    return { s: s, parts: parts };
  }

  /** Deterministic printed-book page sequence (BookEngine.composeBook). */
  function composeBook(j, opts) {
    var photosPerPage = opts && opts.photosPerPage != null ? opts.photosPerPage : CONST.PHOTOS_PER_PAGE_DEFAULT;
    var maxSpreads = opts && opts.maxSpreads != null ? opts.maxSpreads : CONST.MAX_BOOK_SPREADS;
    var pages = [];
    if (!j || !j.photos || !j.photos.length) return pages;

    var sc = j.photos.map(function (p) { return aesthetic(p).s; });
    var all = j.photos.map(function (_, i) { return i; })
      .sort(function (a, b) { return sc[b] - sc[a]; }); // stable — ties keep index order

    // Cover — strongest landscape overall, else best frame.
    var cover = firstWhere(all, function (i) { return j.photos[i].orient === 'land'; }, all[0]);
    pages.push({ type: 'cover', photo: cover });

    // The Journey — overview map + stats + chapters (renderer reads journey directly).
    pages.push({ type: 'journey' });

    var per = clamp(photosPerPage, 1, 8);
    var isTrip = (j.legs || []).length > 1;
    var k;

    if (isTrip && (j.dayLeg || []).length === j.days.length) {
      // ── TRIP MODE: chapters ARE journeys; one map per journey, no per-day spreads.
      var framesPerLeg = tiered(CONST.FRAMES_PER_LEG, j.legs.length);
      for (var li = 0; li < j.legs.length; li++) {
        var legPhotos = [];
        for (var di = 0; di < j.days.length; di++) {
          if (j.dayLeg[di] === li) legPhotos.push.apply(legPhotos, j.days[di].photos);
        }
        if (!legPhotos.length) continue;
        legPhotos.sort(function (a, b) { return sc[b] - sc[a]; });
        var leg = j.legs[li];
        var landL = legPhotos.filter(function (i) { return j.photos[i].orient === 'land'; });
        var heroL = leg.heroPhoto != null ? leg.heroPhoto : (landL.length ? landL[0] : legPhotos[0]);
        pages.push({ type: 'chapter', leg: leg, no: pad2(li + 1), photo: heroL });
        var restL = legPhotos.filter(function (i) { return i !== heroL; }).slice(0, per * framesPerLeg);
        for (k = 0; k < restL.length; k += per) {
          pages.push({ type: 'frames', leg: leg, title: leg.title, photos: restL.slice(k, k + per) });
        }
      }
    } else {
      // ── SINGLE-JOURNEY MODE: chronological day-by-day.
      var framesCap = tiered(CONST.FRAMES_CAP_DAYS, j.days.length);
      var loneDay = j.days.length <= 1; // its map would repeat the overview map —
      for (var dj = 0; dj < j.days.length; dj++) { // reserve a SECOND photo instead
        var day = j.days[dj];
        if (!day.photos.length) continue;
        var ranked = day.photos.slice().sort(function (a, b) { return sc[b] - sc[a]; });
        var landD = ranked.filter(function (i) { return j.photos[i].orient === 'land'; });
        var heroD = landD.length ? landD[0] : ranked[0];
        var secondary = -1;
        if (loneDay) secondary = firstWhere(ranked, function (i) { return i !== heroD; }, -1);
        var pg = { type: 'day', day: day, photo: heroD, title: day.title, body: day.body };
        if (secondary >= 0) pg.support = secondary;
        pages.push(pg);
        var used = new Set([heroD]);
        if (secondary >= 0) used.add(secondary);
        var restD = ranked.filter(function (i) { return !used.has(i); }).slice(0, per * framesCap);
        for (k = 0; k < restD.length; k += per) {
          pages.push({ type: 'frames', day: day, title: day.title, photos: restD.slice(k, k + per) });
        }
      }
    }

    // End — a strong landscape distinct from the cover.
    var end = firstWhere(all, function (i) { return i !== cover && j.photos[i].orient === 'land'; },
      firstWhere(all, function (i) { return i !== cover; }, cover));
    pages.push({ type: 'end', photo: end });

    return capToSpreadBudget(pages, maxSpreads);
  }

  /** Trim surplus frames pages, fattest day/leg group first, weakest page first. */
  function capToSpreadBudget(pages, maxSpreads) {
    if (maxSpreads == null) maxSpreads = CONST.MAX_BOOK_SPREADS;
    if (maxSpreads <= 0 || pages.length <= maxSpreads) return pages;
    function groupOf(p) { return p.type === 'frames' ? (p.leg || p.day || null) : null; }
    var out = pages.slice();
    while (out.length > maxSpreads) {
      var counts = new Map(); // MUST be a Map — keys are the day/leg objects
      for (var i = 0; i < out.length; i++) {
        var g = groupOf(out[i]);
        if (g) counts.set(g, (counts.get(g) || 0) + 1);
      }
      if (!counts.size) break; // only structural pages left
      var top = null, topN = 0;
      counts.forEach(function (n, g2) { if (n > topN) { topN = n; top = g2; } }); // insertion order, strict >
      var idx = -1;
      for (var r = out.length - 1; r >= 0; r--) { // that group's WEAKEST frames page
        if (groupOf(out[r]) === top) { idx = r; break; }
      }
      if (idx < 0) break;
      out.splice(idx, 1);
    }
    return out;
  }

  // ─────────────────── stage 4 — async decoration ───────────────────

  function fetchWithTimeout(url, ms, parentSignal) {
    if (typeof fetch === 'undefined') return Promise.reject(new Error('fetch unavailable'));
    var sig = null, timer = null, onAbort = null, ctrl = null;
    var A = typeof AbortSignal !== 'undefined' ? AbortSignal : null;
    if (A && typeof A.timeout === 'function' && !parentSignal) {
      sig = A.timeout(ms);
    } else if (A && typeof A.timeout === 'function' && typeof A.any === 'function' && parentSignal) {
      sig = A.any([parentSignal, A.timeout(ms)]);
    } else if (typeof AbortController !== 'undefined') {
      // Fallback for engines without AbortSignal.timeout/any.
      ctrl = new AbortController();
      sig = ctrl.signal;
      timer = setTimeout(function () { try { ctrl.abort(); } catch (e) { } }, ms);
      if (parentSignal) {
        if (parentSignal.aborted) { try { ctrl.abort(); } catch (e) { } }
        else {
          onAbort = function () { try { ctrl.abort(); } catch (e) { } };
          parentSignal.addEventListener('abort', onAbort, { once: true });
        }
      }
    }
    var cleanup = function () {
      if (timer) clearTimeout(timer);
      if (parentSignal && onAbort) { try { parentSignal.removeEventListener('abort', onAbort); } catch (e) { } }
    };
    return fetch(url, sig ? { signal: sig } : undefined).then(
      function (r) { cleanup(); return r; },
      function (e) { cleanup(); throw e; }
    );
  }

  function _dateStr(date) {
    if (date == null) return null;
    if (typeof date === 'string') return date; // already 'YYYY-MM-DD'
    var d = date instanceof Date ? date : new Date(date);
    return String(d.getFullYear()).padStart(4, '0') + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }

  /** ONE batched elevation call for every non-null center; failures → nulls. */
  function _fetchElevations(centers, signal) {
    var out = centers.map(function () { return null; });
    var idx = [];
    for (var i = 0; i < centers.length; i++) if (centers[i] != null) idx.push(i);
    if (!idx.length) return Promise.resolve(out);
    var lats = idx.map(function (i2) { return centers[i2][0].toFixed(4); }).join(',');
    var lngs = idx.map(function (i2) { return centers[i2][1].toFixed(4); }).join(',');
    return fetchWithTimeout(CONST.ELEVATION_URL + '?latitude=' + lats + '&longitude=' + lngs,
      CONST.ENRICH_TIMEOUT_MS, signal)
      .then(function (r) {
        if (!r || r.status !== 200) return out;
        return r.json().then(function (data) {
          var el = (data && data.elevation) || [];
          for (var k = 0; k < idx.length; k++) {
            if (k < el.length && el[k] != null) out[idx[k]] = Math.round(el[k]);
          }
          return out;
        });
      })
      .catch(function () { return out; }); // offline / abort / bad JSON — all fine
  }

  /** Historical weather for one day at its centre; any failure → null. */
  function _fetchWeather(center, date, signal) {
    var d = _dateStr(date);
    if (!center || !d) return Promise.resolve(null);
    var url = CONST.ARCHIVE_URL +
      '?latitude=' + center[0].toFixed(4) + '&longitude=' + center[1].toFixed(4) +
      '&start_date=' + d + '&end_date=' + d +
      '&daily=temperature_2m_max,weather_code&timezone=auto';
    return fetchWithTimeout(url, CONST.ENRICH_TIMEOUT_MS, signal)
      .then(function (r) {
        if (!r || r.status !== 200) return null;
        return r.json().then(function (data) {
          var daily = data && data.daily;
          if (!daily) return null;
          var temps = daily.temperature_2m_max;
          var codes = daily.weather_code;
          var temp = (temps && temps.length && temps[0] != null) ? Math.round(temps[0]) : null;
          var code = (codes && codes.length && codes[0] != null) ? Math.trunc(codes[0]) : null;
          var label = wmoLabel(code);
          if (label == null && temp == null) return null;
          if (temp == null) return label;
          if (label == null) return temp + '°';
          return label + ' · ' + temp + '°';
        });
      })
      .catch(function () { return null; });
  }

  /** Port of BookEnrichment.enrich — best-effort, never throws. */
  function enrichJourney(j, opts) {
    var signal = (opts && opts.signal) || null;
    if (!j || !j.days || !j.days.length) return Promise.resolve(j);

    function dayCenter(d) {
      var pts = (d.way && d.way.length) ? d.way : (j.waypoints || []);
      if (!pts.length) return null; // locationless day — never query (0,0)
      var la = 0, lo = 0;
      for (var i = 0; i < pts.length; i++) { la += pts[i].lat; lo += pts[i].lng; }
      return [la / pts.length, lo / pts.length];
    }

    var centers = j.days.map(dayCenter);
    return _fetchElevations(centers, signal).then(function (elevs) {
      return Promise.all(j.days.map(function (d, i) {
        return _fetchWeather(centers[i], d.date, signal);
      })).then(function (wxs) {
        var newDays = j.days.map(function (d, i) {
          var nd = Object.assign({}, d);
          var e = i < elevs.length ? elevs[i] : null;
          if (e != null) nd.elev = e;                 // copyWith: null keeps existing
          var w = i < wxs.length ? wxs[i] : null;
          nd.wx = w != null ? w : d.wx;
          return nd;
        });
        return Object.assign({}, j, { days: newDays });
      });
    }).catch(function () { return j; });
  }

  // ═══════════════════ STUDIO ADAPTER ═══════════════════
  // PhotoMeta (kr-press-core ingest) → the shapes the studio consumes
  // (spec-v3.md / spec-mobile.md §5): StudioPhoto records, CHAPTERS-shaped
  // legs with pools, TRIP_WPS waypoints, reveal data, enrichment cache.

  /**
   * Wall-clock components of a photo's capture time. Never UTC-shifts an EXIF
   * wall time: prefers meta.wall {y,mo,d,h,mi,s}; else shifts the epoch by the
   * photo's own tz offset and reads UTC fields; else local interpretation.
   */
  function wallOf(meta) {
    if (meta.wall && typeof meta.wall.y === 'number') return meta.wall;
    var ms = meta.takenAt;
    if (ms == null) return null;
    if (typeof meta.tzOffsetMin === 'number') {
      var u = new Date(timeMs(ms) + meta.tzOffsetMin * 60000);
      return {
        y: u.getUTCFullYear(), mo: u.getUTCMonth() + 1, d: u.getUTCDate(),
        h: u.getUTCHours(), mi: u.getUTCMinutes(), s: u.getUTCSeconds()
      };
    }
    var l = new Date(timeMs(ms));
    return {
      y: l.getFullYear(), mo: l.getMonth() + 1, d: l.getDate(),
      h: l.getHours(), mi: l.getMinutes(), s: l.getSeconds()
    };
  }

  function wallTs(w) { return Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s || 0); }
  function wallDate(w) { return String(w.y).padStart(4, '0') + '-' + pad2(w.mo) + '-' + pad2(w.d); }

  /**
   * The studio's photo score heuristic (spec-mobile §5 Photo record) — this is
   * what the reasoning chips display: base 0.6 +0.08 landscape + up to +0.12
   * megapixels (cap 16 MP) + 0.07 golden, clamped 0.5–0.92.
   */
  function scorePhoto(w, h, mins) {
    var land = (w === 0 && h === 0) ? true : w >= h;
    var golden = mins != null && (mins <= CONST.GOLDEN_BEFORE_MIN || mins >= CONST.GOLDEN_AFTER_MIN);
    var s = CONST.SCORE_BASE + (land ? CONST.SCORE_LAND_BONUS : 0);
    var mp = (w * h) / 1e6;
    if (mp > 0) s += Math.min(mp, CONST.SCORE_MP_CAP) / CONST.SCORE_MP_CAP * CONST.SCORE_MP_MAX_BONUS;
    if (golden) s += CONST.STUDIO_SCORE_GOLDEN_BONUS;
    return { score: clamp(s, CONST.SCORE_CLAMP[0], CONST.STUDIO_SCORE_MAX), land: land, golden: golden };
  }

  /**
   * PhotoMeta[] → StudioPhoto[] {id, place, slug?, lat, lng, t:'HH:MM',
   * date:'YYYY-MM-DD', golden, land, score, …}. Excludes resolution:'out';
   * 'fixed' photos use their slotHint takenAt/place as if real.
   */
  function toStudioPhotos(metas) {
    var out = [];
    if (!metas) return out;
    for (var i = 0; i < metas.length; i++) {
      var m = metas[i];
      if (!m || m.resolution === 'out') continue;
      var fixed = m.resolution === 'fixed' && m.slotHint;
      var place = m.place != null ? m.place : null;
      var wall = wallOf(m);
      if (fixed) {
        if (m.slotHint.place) place = m.slotHint.place;
        // wallOf prefers the hint's own .wall/.tzOffsetMin frame (added by
        // core's hint builder) so the resolved date/time never shifts with
        // the viewer's zone; hints carrying only takenAt keep working.
        var hintWall = wallOf(m.slotHint);
        if (hintWall != null) wall = hintWall;
      }
      var w = m.w || 0, h = m.h || 0;
      var o = m.orientation || 1;
      if (o >= 5 && o <= 8) { var tmp = w; w = h; h = tmp; } // EXIF rotation swaps axes
      var mins = wall != null ? wall.h * 60 + wall.mi : null;
      var sc = scorePhoto(w, h, mins);
      var hasGps = typeof m.lat === 'number' && typeof m.lon === 'number' &&
        !(m.lat === 0 && m.lon === 0);
      var rec = {
        id: m.id,
        place: place || null,
        cc: m.placeCC || null,
        lat: hasGps ? m.lat : null,
        lng: hasGps ? m.lon : null,
        t: mins != null ? pad2(wall.h) + ':' + pad2(wall.mi) : null,
        date: wall != null ? wallDate(wall) : null,
        ts: wall != null ? wallTs(wall) : null,
        mins: mins,
        golden: sc.golden,
        land: sc.land,
        w: w,
        h: h,
        score: sc.score,
        fixed: !!fixed,
        source: m.source || 'file'
      };
      if (m.slug != null) rec.slug = m.slug;
      out.push(rec);
    }
    return out;
  }

  /**
   * Place clustering for arbitrary uploads: DBSCAN eps 40 km with the Dart
   * bbox prefilter, but minPts ADAPTED DOWN for small sets —
   * max(3, min(8, floor(n/12))) — so a 60-photo, 5-place trip still clusters
   * (deviation from the Dart fixed 8, which needs ≥9 co-located photos).
   * Photos DBSCAN calls noise are folded in afterwards: joined to the nearest
   * cluster when within 1.5×eps, else grouped by a deterministic 40 km
   * leader pass — every GPS photo ends up in exactly one cluster.
   */
  function clusterPlaces(photos, opts) {
    var pts = (photos || []).filter(function (p) { return p && p.lat != null && p.lng != null; });
    var n = pts.length;
    if (!n) return [];
    var epsKm = (opts && opts.epsKm) || CONST.DBSCAN_EPS_METERS / 1000;
    var minPts = (opts && opts.minPts) || Math.max(3, Math.min(CONST.DBSCAN_MIN_PHOTOS, Math.floor(n / 12)));

    var visited = new Array(n).fill(false);
    var clusterId = new Array(n).fill(-1);
    var nextCluster = 0;

    function regionQuery(i) {
      var neighbors = [];
      for (var j = 0; j < n; j++) {
        if (i === j) continue;
        if (Math.abs(pts[j].lat - pts[i].lat) > CONST.BBOX_PREFILTER_LAT_DEG) continue;
        if (Math.abs(pts[j].lng - pts[i].lng) > CONST.BBOX_PREFILTER_LNG_DEG) continue;
        if (haversineKm(pts[i].lat, pts[i].lng, pts[j].lat, pts[j].lng) <= epsKm) neighbors.push(j);
      }
      return neighbors;
    }

    for (var i = 0; i < n; i++) {
      if (visited[i]) continue;
      visited[i] = true;
      var neighbors = regionQuery(i);
      if (neighbors.length < minPts) continue;
      var cid = nextCluster++;
      clusterId[i] = cid;
      var queue = neighbors.slice();
      while (queue.length) {
        var j = queue.pop();
        if (!visited[j]) {
          visited[j] = true;
          var nj = regionQuery(j);
          if (nj.length >= minPts) queue.push.apply(queue, nj);
        }
        if (clusterId[j] === -1) clusterId[j] = cid;
      }
    }

    var groups = [];
    for (var c = 0; c < nextCluster; c++) groups.push([]);
    var noise = [];
    for (var k = 0; k < n; k++) {
      if (clusterId[k] >= 0) groups[clusterId[k]].push(k);
      else noise.push(k);
    }

    function meanOf(idxs) {
      var la = 0, lo = 0;
      for (var m = 0; m < idxs.length; m++) { la += pts[idxs[m]].lat; lo += pts[idxs[m]].lng; }
      return { lat: la / idxs.length, lng: lo / idxs.length };
    }

    // Fold noise back in (deviation from Dart — the studio needs total coverage).
    var frozen = groups.map(function (g) { return g.length ? meanOf(g) : null; });
    var leaders = [];
    for (var z = 0; z < noise.length; z++) {
      var p = pts[noise[z]];
      var bi = -1, bd = Infinity;
      for (var c2 = 0; c2 < frozen.length; c2++) {
        if (!frozen[c2]) continue;
        var d = haversineKm(p.lat, p.lng, frozen[c2].lat, frozen[c2].lng);
        if (d < bd) { bd = d; bi = c2; }
      }
      if (bi >= 0 && bd <= epsKm * 1.5) { groups[bi].push(noise[z]); continue; }
      var li = -1;
      for (var l = 0; l < leaders.length; l++) {
        if (haversineKm(p.lat, p.lng, leaders[l].lat, leaders[l].lng) <= epsKm) { li = l; break; }
      }
      if (li >= 0) leaders[li].idxs.push(noise[z]);
      else leaders.push({ lat: p.lat, lng: p.lng, idxs: [noise[z]] });
    }
    for (var l2 = 0; l2 < leaders.length; l2++) groups.push(leaders[l2].idxs);

    var out = [];
    for (var g2 = 0; g2 < groups.length; g2++) {
      if (!groups[g2].length) continue;
      var center = meanOf(groups[g2]);
      var placeCounts = new Map();
      var ids = [];
      for (var m2 = 0; m2 < groups[g2].length; m2++) {
        var ph = pts[groups[g2][m2]];
        ids.push(ph.id);
        if (ph.place) placeCounts.set(ph.place, (placeCounts.get(ph.place) || 0) + 1);
      }
      out.push({
        id: 'pl_' + out.length,
        name: placeCounts.size ? firstMaxKey(placeCounts) : null,
        lat: center.lat,
        lng: center.lng,
        count: groups[g2].length,
        ids: ids
      });
    }
    return out;
  }

  /** Group studio photos by wall-clock calendar date → [{date, ids}] (dated only). */
  function groupDays(photos) {
    var sorted = (photos || []).filter(function (p) { return p && p.date != null; })
      .slice().sort(function (a, b) { return a.ts - b.ts; });
    var byDate = new Map();
    for (var i = 0; i < sorted.length; i++) {
      if (!byDate.has(sorted[i].date)) byDate.set(sorted[i].date, []);
      byDate.get(sorted[i].date).push(sorted[i].id);
    }
    var out = [];
    byDate.forEach(function (ids, date) { out.push({ date: date, ids: ids }); });
    out.sort(function (a, b) { return a.date < b.date ? -1 : a.date > b.date ? 1 : 0; });
    return out;
  }

  // ── leg detection ──

  function _gpsCentroid(photos) {
    var la = 0, lo = 0, n = 0;
    for (var i = 0; i < photos.length; i++) {
      if (photos[i].lat != null && photos[i].lng != null) { la += photos[i].lat; lo += photos[i].lng; n++; }
    }
    return n ? { lat: la / n, lng: lo / n } : null;
  }

  function _dominantCluster(photos, clusterIdx) {
    var counts = new Map();
    for (var i = 0; i < photos.length; i++) {
      var c = clusterIdx[photos[i].id];
      if (c != null) counts.set(c, (counts.get(c) || 0) + 1);
    }
    var best = firstMaxKey(counts);
    return best == null ? -1 : best;
  }

  function _placeCounts(photos) {
    var counts = new Map();
    for (var i = 0; i < photos.length; i++) {
      if (photos[i].place) counts.set(photos[i].place, (counts.get(photos[i].place) || 0) + 1);
    }
    return counts;
  }

  function _countryDisplay(cc) {
    if (!cc) return null;
    try {
      if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
        var dn = new Intl.DisplayNames(['en'], { type: 'region' });
        var name = dn.of(String(cc).toUpperCase());
        if (name && name !== String(cc).toUpperCase()) return name;
      }
    } catch (e) { }
    return null;
  }

  /**
   * Leg detection for arbitrary uploads (browser has no home pin, so the Dart
   * away-period path can't run): cluster places, split the dated timeline
   * into fragments at calendar-day boundaries or >6h gaps, then merge
   * consecutive fragments whose dominant clusters sit within 120 km — a leg
   * boundary therefore needs BOTH a cluster change (>120 km) AND a day
   * boundary or >6h gap. Chain order preserved: a mid-trip side-hop stays a
   * contiguous leg of its own, exactly like Dart buildTrip's stitching.
   */
  function _buildLegs(photos) {
    var placed = [], gpsOnly = [], timedOnly = [], unplaced = [];
    for (var i = 0; i < (photos || []).length; i++) {
      var p = photos[i];
      if (!p) continue;
      var hasGps = p.lat != null && p.lng != null;
      var hasTime = p.ts != null;
      if (hasGps && hasTime) placed.push(p);
      else if (hasGps) gpsOnly.push(p);
      else if (hasTime) timedOnly.push(p);
      else unplaced.push(p);
    }

    function mkLeg() {
      return { merged: [], extras: [], start: null, end: null, fallbackTitle: null };
    }

    // Degenerate: nothing has GPS or time → one 'Your trip' chapter, ingest order.
    if (!placed.length && !gpsOnly.length && !timedOnly.length) {
      if (!unplaced.length) return { legs: [], elsewhere: [], clusters: [] };
      var lone = mkLeg();
      lone.merged = unplaced.slice();
      lone.fallbackTitle = 'Your trip';
      return { legs: [_finalizeLeg(lone)], elsewhere: [], clusters: [] };
    }

    var gpsPhotos = placed.concat(gpsOnly);
    var clusters = clusterPlaces(gpsPhotos);
    var clusterIdx = {};
    for (var c = 0; c < clusters.length; c++) {
      for (var k = 0; k < clusters[c].ids.length; k++) clusterIdx[clusters[c].ids[k]] = c;
    }

    var legs = [];

    if (placed.length) {
      var sorted = placed.slice().sort(function (a, b) { return a.ts - b.ts; });
      // Fragments: split at calendar-day change or >6h gap.
      var frags = [[sorted[0]]];
      for (var s = 1; s < sorted.length; s++) {
        var prev = frags[frags.length - 1][frags[frags.length - 1].length - 1];
        if (sorted[s].date !== prev.date ||
          (sorted[s].ts - prev.ts) > CONST.LEG_GAP_HOURS * MS_HOUR) {
          frags.push([sorted[s]]);
        } else {
          frags[frags.length - 1].push(sorted[s]);
        }
      }
      var units = frags.map(function (f) {
        return { photos: f, dom: _dominantCluster(f, clusterIdx) };
      });
      var cur = mkLeg();
      cur.merged = units[0].photos.slice();
      var curDom = units[0].dom;
      for (var u = 1; u < units.length; u++) {
        var a = curDom, b = units[u].dom;
        var distSplit = a !== b && a >= 0 && b >= 0 &&
          haversineKm(clusters[a].lat, clusters[a].lng, clusters[b].lat, clusters[b].lng) > CONST.LEG_SPLIT_KM;
        // Distance alone misses the "same place, years apart" case — a photo
        // LIBRARY of repeat visits to one favourite region (no >120km jump
        // ever happens) reads as one giant leg spanning years, which then
        // auto-switches to days-as-chapters and leaves every date without
        // its own 2+ waypoints falling back to that whole-leg map — so two
        // trips a decade apart render an identical, misleadingly-dated map.
        // units are already in ts order (built straight off `frags`), so
        // comparing consecutive fragments' end/start ts directly finds a
        // real gap regardless of whether the dominant cluster changed.
        var prevPh = units[u - 1].photos, prevEndTs = prevPh[prevPh.length - 1].ts;
        var curStartTs = units[u].photos[0].ts;
        var gapSplit = (prevEndTs != null && curStartTs != null) &&
          (curStartTs - prevEndTs) > CONST.LEG_SPLIT_GAP_DAYS * MS_DAY;
        var split = distSplit || gapSplit;
        if (split) {
          legs.push(cur);
          cur = mkLeg();
          cur.merged = units[u].photos.slice();
          curDom = units[u].dom;
        } else {
          cur.merged.push.apply(cur.merged, units[u].photos);
          if (curDom < 0) curDom = units[u].dom;
        }
      }
      legs.push(cur);
    } else if (clusters.length) {
      // GPS-only library (no timestamps): one leg per place cluster, cluster order.
      for (var c2 = 0; c2 < clusters.length; c2++) {
        var idSet = new Set(clusters[c2].ids);
        var lg = mkLeg();
        lg.merged = gpsOnly.filter(function (p2) { return idSet.has(p2.id); });
        legs.push(lg);
      }
    } else if (timedOnly.length) {
      // Pure-timeline trip: dated photos, ZERO gps anywhere in the whole
      // library (old scans, screenshots, metadata-stripped imports). Same
      // reasoning as the gap-split above: a photo LIBRARY of separate
      // visits — no GPS to distance-split on, but real date gaps between
      // clusters — shouldn't collapse into one "Your trip" leg spanning
      // however many years just because there's no coordinate to measure
      // distance from. Chain consecutive-in-time photos the same way the
      // orphan-attach step below does: a gap over LEG_SPLIT_GAP_DAYS starts
      // a new leg. (If this produced only one leg — the common case, an
      // actual single trip with no GPS — behaviour is identical to before.)
      var tlSorted = timedOnly.slice().sort(function (a3, b3) { return a3.ts - b3.ts; });
      var tlLeg = mkLeg();
      tlLeg.merged = [tlSorted[0]];
      legs.push(tlLeg);
      for (var tlI = 1; tlI < tlSorted.length; tlI++) {
        if ((tlSorted[tlI].ts - tlSorted[tlI - 1].ts) > CONST.LEG_SPLIT_GAP_DAYS * MS_DAY) {
          var newTlLeg = mkLeg();
          newTlLeg.merged = [tlSorted[tlI]];
          legs.push(newTlLeg);
        } else {
          legs[legs.length - 1].merged.push(tlSorted[tlI]);
        }
      }
      timedOnly = [];  // fully consumed above — the attach/orphan step below must not re-process these
    }

    // GPS-but-undated photos: append to the leg that shares their place cluster
    // (else the leg with the nearest centroid).
    if (placed.length && gpsOnly.length) {
      var legDoms = legs.map(function (lg2) { return _dominantCluster(lg2.merged, clusterIdx); });
      var legCenters = legs.map(function (lg3) { return _gpsCentroid(lg3.merged); });
      for (var g = 0; g < gpsOnly.length; g++) {
        var gp = gpsOnly[g];
        var ccl = clusterIdx[gp.id];
        var target = -1;
        for (var lgI = 0; lgI < legs.length; lgI++) {
          if (legDoms[lgI] === ccl) { target = lgI; break; }
        }
        if (target < 0) {
          var bd = Infinity;
          for (var lgJ = 0; lgJ < legs.length; lgJ++) {
            if (!legCenters[lgJ]) continue;
            var dd = haversineKm(gp.lat, gp.lng, legCenters[lgJ].lat, legCenters[lgJ].lng);
            if (dd < bd) { bd = dd; target = lgJ; }
          }
        }
        if (target < 0) target = 0;
        legs[target].extras.push(gp);
      }
    }

    // Leg time ranges (before timed-only insertion).
    for (var lr = 0; lr < legs.length; lr++) {
      var mm = legs[lr].merged;
      if (mm.length && mm[0].ts != null) {
        legs[lr].start = mm[0].ts;
        legs[lr].end = mm[mm.length - 1].ts;
      }
    }

    // Timed-but-GPS-less photos (incl. slotted 'fixed' ones): attach to the
    // temporally NEAREST leg — but only if it's actually close in time.
    // Without a distance check here, a photo from years before/after every
    // GPS-anchored leg still gets glued onto "whichever leg happens to be
    // least-far", silently stretching that leg's displayed date range by
    // years (this is what let a solo 2006 photo drag a 2015 leg's dates
    // back to 2006, defeating the gap-split above). Orphans beyond
    // LEG_SPLIT_GAP_DAYS of every leg become their OWN new leg(s) instead —
    // grouped among themselves the same way (consecutive-gap chaining), so
    // e.g. three 2024 photos taken days apart form one new leg together,
    // while a lone 2006 or 2013 photo each gets its own.
    var gapMs = CONST.LEG_SPLIT_GAP_DAYS * MS_DAY;
    function _attachTimeOrdered(leg, tp) {
      var arr = leg.merged;
      var at = arr.length;
      for (var aI = 0; aI < arr.length; aI++) {
        if (arr[aI].ts != null && arr[aI].ts > tp.ts) { at = aI; break; }
      }
      arr.splice(at, 0, tp);
    }
    if (timedOnly.length && legs.length) {
      var timedSorted = timedOnly.slice().sort(function (a2, b2) { return a2.ts - b2.ts; });
      var orphans = [];
      // If NO leg has an established time range yet (a pure-timeline trip
      // with zero GPS anywhere, or a GPS-but-timestampless cluster), there's
      // nothing to measure a "gap" against — fall back to the pre-fix
      // behaviour of attaching everything to the first leg, same as before
      // this whole gap-aware block existed.
      var anyAnchored = legs.some(function (lg0) { return lg0.start != null; });
      for (var tI = 0; tI < timedSorted.length; tI++) {
        var tp = timedSorted[tI];
        if (!anyAnchored) { _attachTimeOrdered(legs[0], tp); continue; }
        var bi2 = -1, bd2 = Infinity;
        for (var lgK = 0; lgK < legs.length; lgK++) {
          var st = legs[lgK].start, en = legs[lgK].end;
          if (st == null) continue;   // this particular leg has no anchor to compare against
          var d2 = tp.ts < st ? st - tp.ts : (tp.ts > en ? tp.ts - en : 0);
          if (d2 < bd2) { bd2 = d2; bi2 = lgK; }
        }
        if (bi2 < 0 || bd2 > gapMs) { orphans.push(tp); continue; }
        _attachTimeOrdered(legs[bi2], tp);
      }
      // orphans are still ts-sorted (filtered from timedSorted in order) —
      // chain consecutive ones into new legs, splitting on the same gap.
      for (var oI = 0; oI < orphans.length; oI++) {
        var lastLeg = legs.length ? legs[legs.length - 1] : null;
        var lastLegIsOrphan = lastLeg && lastLeg._orphan;
        var prevOrphanTs = lastLegIsOrphan ? lastLeg.merged[lastLeg.merged.length - 1].ts : null;
        if (lastLegIsOrphan && prevOrphanTs != null && (orphans[oI].ts - prevOrphanTs) <= gapMs) {
          lastLeg.merged.push(orphans[oI]);
        } else {
          var newLeg = mkLeg();
          newLeg.merged = [orphans[oI]];
          newLeg._orphan = true;
          legs.push(newLeg);
        }
      }
      legs.forEach(function (lg) { delete lg._orphan; });
    }

    return {
      legs: legs.map(_finalizeLeg),
      elsewhere: unplaced.map(function (p3) { return p3.id; }),
      clusters: clusters
    };
  }

  function _finalizeLeg(leg) {
    var pool = leg.merged.concat(leg.extras); // time order, then undated extras
    var days = [];
    var byDate = new Map();
    for (var i = 0; i < leg.merged.length; i++) {
      var p = leg.merged[i];
      if (p.date == null) continue;
      if (!byDate.has(p.date)) {
        var day = { date: p.date, photos: [] };
        byDate.set(p.date, day);
        days.push(day);
      }
      byDate.get(p.date).photos.push(p);
    }
    days.sort(function (a, b) { return a.date < b.date ? -1 : a.date > b.date ? 1 : 0; });
    var centroid = _gpsCentroid(pool);
    for (var d = 0; d < days.length; d++) {
      days[d].centroid = _gpsCentroid(days[d].photos) || centroid;
    }
    return {
      pool: pool,
      days: days,
      centroid: centroid,
      placeCounts: _placeCounts(pool),
      ccCounts: (function () {
        var counts = new Map();
        for (var c = 0; c < pool.length; c++) {
          if (pool[c].cc) counts.set(pool[c].cc, (counts.get(pool[c].cc) || 0) + 1);
        }
        return counts;
      })(),
      start: leg.merged.length && leg.merged[0].ts != null ? leg.merged[0].ts : null,
      end: leg.merged.length && leg.merged[leg.merged.length - 1].ts != null
        ? leg.merged[leg.merged.length - 1].ts : null,
      fallbackTitle: leg.fallbackTitle
    };
  }

  function _parseDate(s) { // 'YYYY-MM-DD' → {y, mo, d}
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    return m ? { y: +m[1], mo: +m[2], d: +m[3] } : null;
  }

  /** '3 AUG' | '3–7 AUG' | '28 JUL – 2 AUG' | '3 AUG 2018 – 2 JAN 2019'. */
  function _fmtDatesShort(a, b) {
    if (!a && !b) return '';
    if (!a) a = b;
    if (!b) b = a;
    var ma = MON[a.mo - 1].toUpperCase(), mb = MON[b.mo - 1].toUpperCase();
    if (a.y === b.y && a.mo === b.mo) {
      if (a.d === b.d) return a.d + ' ' + ma;
      return a.d + '–' + b.d + ' ' + ma;
    }
    if (a.y === b.y) return a.d + ' ' + ma + ' – ' + b.d + ' ' + mb;
    return a.d + ' ' + ma + ' ' + a.y + ' – ' + b.d + ' ' + mb + ' ' + b.y;
  }

  /** 'AUG 3, 2025' | 'AUG 3 – 23, 2025' | 'AUG 3 – SEP 2, 2025' | cross-year. */
  function _fmtRangeLong(a, b) {
    if (!a && !b) return '';
    if (!a) a = b;
    if (!b) b = a;
    var ma = MON[a.mo - 1].toUpperCase(), mb = MON[b.mo - 1].toUpperCase();
    if (a.y === b.y && a.mo === b.mo) {
      if (a.d === b.d) return ma + ' ' + a.d + ', ' + a.y;
      return ma + ' ' + a.d + ' – ' + b.d + ', ' + a.y;
    }
    if (a.y === b.y) return ma + ' ' + a.d + ' – ' + mb + ' ' + b.d + ', ' + a.y;
    return ma + ' ' + a.d + ', ' + a.y + ' – ' + mb + ' ' + b.d + ', ' + b.y;
  }

  function _slug(s) {
    var t = String(s == null ? '' : s);
    if (t.normalize) t = t.normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '');
    t = t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return t || 'chapter';
  }

  function _uniqueId(base, used) {
    var id = base, n = 2;
    while (used.has(id)) { id = base + '-' + n; n++; }
    used.add(id);
    return id;
  }

  function _legTitle(leg) {
    var top = firstMaxKey(leg.placeCounts);
    if (top) return leg.placeCounts.size > 1 ? top + ' & around' : top;
    if (leg.fallbackTitle) return leg.fallbackTitle;
    var country = _countryDisplay(firstMaxKey(leg.ccCounts));
    return country || 'Your trip';
  }

  function _legKm(pool) {
    var km = 0, last = null;
    for (var i = 0; i < pool.length; i++) {
      var p = pool[i];
      if (p.lat == null || p.lng == null) continue;
      if (last) km += haversineKm(last.lat, last.lng, p.lat, p.lng);
      last = p;
    }
    return km;
  }

  function _legWx(dates, wxByDate) {
    if (!wxByDate || !dates.length) return null;
    var mid = dates[Math.floor((dates.length - 1) / 2)];
    if (wxByDate[mid] != null) return wxByDate[mid];
    for (var i = 0; i < dates.length; i++) if (wxByDate[dates[i]] != null) return wxByDate[dates[i]];
    return null;
  }

  function _legElev(dates, elevByDate) {
    if (!elevByDate) return null;
    var best = null;
    for (var i = 0; i < dates.length; i++) {
      var e = elevByDate[dates[i]];
      if (e != null && (best == null || e > best)) best = e; // elevation band = the peak
    }
    return best;
  }

  /**
   * Waypoints for a set of trails ({photos, leg} per chapter): the Dart 1.2 km
   * merge + nearest-photo-place naming, capped at ~24 total by raising the
   * merge distance iteratively (×1.6) until the chain fits.
   */
  function _buildWaypoints(trails, cap) {
    if (cap == null) cap = CONST.WAYPOINT_CAP;
    var merge = CONST.WAYPOINT_MERGE_KM;
    var wps = [];
    for (; ;) {
      wps = [];
      for (var t = 0; t < trails.length; t++) {
        var trail = trails[t].photos;
        if (!trail.length) continue;
        var nodes = simplifyNodes(trail, merge);
        for (var nI = 0; nI < nodes.length; nI++) {
          var best = null, bestD = Infinity;
          for (var pI = 0; pI < trail.length; pI++) {
            var d = haversineKm(nodes[nI].lat, nodes[nI].lng, trail[pI].lat, trail[pI].lng);
            if (d < bestD) { bestD = d; best = trail[pI]; }
          }
          var name = best && best.place ? titleCase(best.place) : null;
          wps.push({
            name: name != null ? name : 'Stop ' + (wps.length + 1),
            lat: nodes[nI].lat,
            lng: nodes[nI].lng,
            leg: trails[t].leg
          });
        }
      }
      if (wps.length <= cap || merge > 2000) break;
      merge *= 1.6;
    }
    return wps;
  }

  function _legTrail(leg) {
    // Route trail = dated GPS photos in time order; fall back to undated GPS.
    var timed = leg.pool.filter(function (p) { return p.lat != null && p.lng != null && p.ts != null; });
    if (timed.length) return timed;
    return leg.pool.filter(function (p) { return p.lat != null && p.lng != null; });
  }

  function _emptyResult() {
    return {
      chapters: [], pools: [], waypoints: [], elsewhere: [],
      totals: { km: 0, days: 0, places: 0, photos: 0, dateRange: '' }
    };
  }

  /**
   * StudioPhoto[] → {chapters, pools, waypoints, totals, elsewhere}.
   * mode: 'trip' (legs = chapters) or {journey: i} (days-as-chapters within
   * leg i). opts may carry a cached enrichment {wxByDate, elevByDate}.
   */
  function deriveChapters(photos, mode, opts) {
    photos = photos || [];
    opts = opts || {};
    if (!photos.length) return _emptyResult();

    var built = _buildLegs(photos);
    if (!built.legs.length) {
      var empty = _emptyResult();
      empty.elsewhere = built.elsewhere;
      empty.totals.photos = photos.length;
      return empty;
    }

    if (mode && typeof mode === 'object' && typeof mode.journey === 'number') {
      return _journeyChapters(built, mode.journey, opts, photos);
    }
    return _tripChapters(built, opts, photos);
  }

  function _tripChapters(built, opts, photos) {
    var usedIds = new Set();
    var wxByDate = opts.wxByDate || null;
    var elevByDate = opts.elevByDate || null;

    var chapters = built.legs.map(function (leg, li) {
      var title = _legTitle(leg);
      var dates = leg.days.map(function (d) { return d.date; });
      var a = dates.length ? _parseDate(dates[0]) : null;
      var b = dates.length ? _parseDate(dates[dates.length - 1]) : null;
      var km = _legKm(leg.pool);
      return {
        id: _uniqueId('ch-' + _slug(title), usedIds),
        num: pad2(li + 1),
        title: title,
        dates: _fmtDatesShort(a, b),
        days: leg.days.length,
        frames: leg.pool.length,
        places: leg.placeCounts.size,
        pools: leg.pool.map(function (p) { return p.id; }),
        st: {
          km: km < 1 ? null : Math.round(km), // hidden when the leg barely moves
          wx: _legWx(dates, wxByDate),
          elev: _legElev(dates, elevByDate),
          coords: leg.centroid ? dms(leg.centroid.lat, true) + ' · ' + dms(leg.centroid.lng, false) : ''
        },
        dayInfo: leg.days.map(function (d) {
          return {
            date: d.date,
            lat: d.centroid ? d.centroid.lat : null,
            lng: d.centroid ? d.centroid.lng : null
          };
        })
      };
    });

    var waypoints = _buildWaypoints(built.legs.map(function (leg, li) {
      return { photos: _legTrail(leg), leg: li };
    }));

    // Totals across the whole draft.
    var allDates = new Set(), allPlaces = new Set();
    for (var i = 0; i < built.legs.length; i++) {
      var pool = built.legs[i].pool;
      for (var k = 0; k < pool.length; k++) {
        if (pool[k].date) allDates.add(pool[k].date);
        if (pool[k].place) allPlaces.add(pool[k].place);
      }
    }
    var sortedDates = Array.from(allDates).sort();
    var totals = {
      km: Math.round(routeKm(waypoints)), // waypoint chain, like Dart buildTrip
      days: sortedDates.length,
      places: allPlaces.size,
      photos: photos.length,
      dateRange: sortedDates.length
        ? _fmtRangeLong(_parseDate(sortedDates[0]), _parseDate(sortedDates[sortedDates.length - 1]))
        : ''
    };

    return {
      chapters: chapters,
      pools: chapters.map(function (ch) { return ch.pools; }),
      waypoints: waypoints,
      totals: totals,
      elsewhere: built.elsewhere
    };
  }

  /** Days-as-chapters within one leg (spec-mobile §5 BOOK single-journey mode). */
  function _journeyChapters(built, legIndex, opts, photos) {
    var leg = built.legs[legIndex];
    if (!leg) {
      var empty = _emptyResult();
      empty.elsewhere = built.elsewhere;
      empty.totals.photos = photos.length;
      return empty;
    }
    var usedIds = new Set();
    var wxByDate = opts.wxByDate || null;
    var elevByDate = opts.elevByDate || null;
    var legTitle = _legTitle(leg);

    var chapters = leg.days.map(function (day, di) {
      // Day title: top places joined with '&' (dayopen semantics).
      var counts = _placeCounts(day.photos);
      var entries = [];
      counts.forEach(function (n, name) { entries.push([name, n]); });
      entries.sort(function (x, y) { return y[1] - x[1]; }); // stable — first-seen wins ties
      var title = entries.length
        ? entries.slice(0, 2).map(function (e) { return e[0]; }).join(' & ')
        : legTitle;
      var km = _legKm(day.photos);
      var pd = _parseDate(day.date);
      return {
        id: _uniqueId('ch-' + _slug(title), usedIds),
        num: pad2(di + 1),
        title: title,
        dates: _fmtDatesShort(pd, pd),
        days: 1,
        frames: day.photos.length,
        places: counts.size,
        pools: day.photos.map(function (p) { return p.id; }),
        st: {
          km: km < 1 ? null : Math.round(km),
          wx: wxByDate && wxByDate[day.date] != null ? wxByDate[day.date] : null,
          elev: elevByDate && elevByDate[day.date] != null ? elevByDate[day.date] : null,
          coords: day.centroid ? dms(day.centroid.lat, true) + ' · ' + dms(day.centroid.lng, false) : ''
        },
        dayInfo: [{
          date: day.date,
          lat: day.centroid ? day.centroid.lat : null,
          lng: day.centroid ? day.centroid.lng : null
        }]
      };
    });

    var waypoints = _buildWaypoints(leg.days.map(function (day, di) {
      return {
        photos: day.photos.filter(function (p) { return p.lat != null && p.lng != null; }),
        leg: di
      };
    }));

    var dates = leg.days.map(function (d) { return d.date; });
    var totals = {
      km: Math.round(routeKm(waypoints)),
      days: leg.days.length,
      places: leg.placeCounts.size,
      photos: leg.pool.length,
      dateRange: dates.length
        ? _fmtRangeLong(_parseDate(dates[0]), _parseDate(dates[dates.length - 1]))
        : ''
    };

    return {
      chapters: chapters,
      pools: chapters.map(function (ch) { return ch.pools; }),
      waypoints: waypoints,
      totals: totals,
      elsewhere: built.elsewhere
    };
  }

  /**
   * Onboarding reveal: title guess, trip meta chips, chapter cards.
   * Deterministic — no randomness, no clock.
   */
  function composeReveal(photos) {
    photos = photos || [];
    if (!photos.length) {
      return { titleGuess: 'Your trip', subtitle: '', tripMeta: [], chapters: [] };
    }
    var r = deriveChapters(photos, 'trip');
    var chapters = r.chapters;

    // Dominant country (display name via Intl.DisplayNames) / dominant place.
    var ccCounts = new Map(), placeCounts = new Map();
    for (var i = 0; i < photos.length; i++) {
      if (photos[i].cc) ccCounts.set(photos[i].cc, (ccCounts.get(photos[i].cc) || 0) + 1);
      if (photos[i].place) placeCounts.set(photos[i].place, (placeCounts.get(photos[i].place) || 0) + 1);
    }
    var country = _countryDisplay(firstMaxKey(ccCounts));
    var domPlace = firstMaxKey(placeCounts);

    var titleGuess;
    if (chapters.length > 1 && country) titleGuess = country;       // 'Indonesia'
    else titleGuess = domPlace || country || 'Your trip';           // one-place trip → the place

    function baseTitle(t) { return String(t).replace(/ & around$/, ''); }
    var subtitle = '';
    if (chapters.length > 2) {
      var a = baseTitle(chapters[0].title), b = baseTitle(chapters[chapters.length - 1].title);
      subtitle = (a && b && a !== b) ? a + ' to ' + b : '';
    }
    if (!subtitle) {
      // Date-based subtitle: 'August 2025' / 'Aug 2025 – Sep 2025'.
      var dated = [];
      for (var d = 0; d < photos.length; d++) if (photos[d].date) dated.push(photos[d].date);
      dated.sort();
      if (dated.length) {
        var pa = _parseDate(dated[0]), pb = _parseDate(dated[dated.length - 1]);
        subtitle = datesLong(new Date(pa.y, pa.mo - 1, pa.d), new Date(pb.y, pb.mo - 1, pb.d));
      }
    }

    var tripMeta = [];
    if (r.totals.dateRange) tripMeta.push({ val: r.totals.dateRange, label: '' });
    if (r.totals.days > 0) {
      tripMeta.push({ val: String(r.totals.days), label: r.totals.days === 1 ? 'DAY' : 'DAYS' });
    }
    if (chapters.length > 0) {
      tripMeta.push({ val: String(chapters.length), label: chapters.length === 1 ? 'JOURNEY' : 'JOURNEYS' });
    }
    if (r.totals.places > 0) {
      tripMeta.push({ val: String(r.totals.places), label: r.totals.places === 1 ? 'PLACE' : 'PLACES' });
    }
    tripMeta.push({ val: String(r.totals.photos), label: 'PHOTOS IN DRAFT' });

    var cards = chapters.map(function (ch, ci) {
      var parts = [];
      if (ch.dates) parts.push(ch.dates);
      parts.push(ch.frames + (ch.frames === 1 ? ' PHOTO' : ' PHOTOS'));
      if (ch.places > 0) parts.push(ch.places + (ch.places === 1 ? ' PLACE' : ' PLACES'));
      return {
        num: ch.num,
        title: ch.title,
        col: TERRACOTTA[ci % TERRACOTTA.length],
        meta: parts.join(' · '),
        photoIds: ch.pools.slice(0, 5)
      };
    });

    return { titleGuess: titleGuess, subtitle: subtitle, tripMeta: tripMeta, chapters: cards };
  }

  /**
   * Studio enrichment: per chapter-day centroid, fetch the Open-Meteo archive
   * (daily weather_code + temperature_2m_max) and elevation, 6 s timeout each.
   * Resolves {wxByDate, elevByDate} for the caller to cache into the project —
   * fetched once, printed forever. NEVER rejects; partial results on failure.
   */
  function enrich(chapters, opts) {
    opts = opts || {};
    var signal = opts.signal || null;
    var out = { wxByDate: {}, elevByDate: {} };
    var list = chapters && chapters.chapters ? chapters.chapters : chapters; // accept the whole result too
    if (!list || !list.length) return Promise.resolve(out);

    var entries = [];
    var seen = new Set();
    for (var c = 0; c < list.length; c++) {
      var di = list[c] && list[c].dayInfo;
      if (!di) continue;
      for (var d = 0; d < di.length; d++) {
        var e = di[d];
        if (!e || !e.date || e.lat == null || e.lng == null || seen.has(e.date)) continue;
        seen.add(e.date);
        entries.push(e);
      }
    }
    if (!entries.length) return Promise.resolve(out);

    var centers = entries.map(function (e2) { return [e2.lat, e2.lng]; });
    return _fetchElevations(centers, signal).then(function (elevs) {
      for (var k = 0; k < entries.length; k++) {
        if (elevs[k] != null) out.elevByDate[entries[k].date] = elevs[k];
      }
      return Promise.all(entries.map(function (e3, i2) {
        return _fetchWeather(centers[i2], e3.date, signal);
      }));
    }).then(function (wxs) {
      for (var k2 = 0; k2 < entries.length; k2++) {
        if (wxs[k2] != null) out.wxByDate[entries[k2].date] = wxs[k2];
      }
      return out;
    }).catch(function () { return out; });
  }

  function simplifyWaypoints(points, mergeKm) { return simplifyNodes(points, mergeKm); }

  // ───────────────────────── public surface ─────────────────────────

  var api = {
    // studio entry points (ARCHITECTURE.md §Engine)
    toStudioPhotos: toStudioPhotos,
    deriveChapters: deriveChapters,
    composeReveal: composeReveal,
    enrich: enrich,

    // exported pure helpers for the studio
    haversineKm: haversineKm,
    simplifyWaypoints: simplifyWaypoints,
    groupDays: groupDays,
    scorePhoto: scorePhoto,
    clusterPlaces: clusterPlaces,

    // stage 1 — library analysis (Dart port)
    detectAwayPeriods: detectAwayPeriods,
    periodForPhoto: periodForPhoto,
    detectPlaceClusters: detectPlaceClusters,
    // stage 2 — journey → book input
    buildJourney: buildJourney,
    buildTrip: buildTrip,
    // stage 3 — composition (pure, deterministic)
    aesthetic: aesthetic,
    composeBook: composeBook,
    capToSpreadBudget: capToSpreadBudget,
    // stage 4 — async decoration
    enrichJourney: enrichJourney,

    util: {
      haversineKm: haversineKm,
      simplifyNodes: simplifyNodes,
      routeKm: routeKm,
      minutesOfDay: minutesOfDay,
      titleCase: titleCase,
      dms: dms,
      datesLong: datesLong,
      datesCompact: datesCompact,
      wmoLabel: wmoLabel,
      clamp: clamp,
      pad2: pad2,
      wallOf: wallOf
    },
    regions: {
      loadCountries: loadCountries,
      countryForPoint: countryForPoint,
      countryCentroid: countryCentroid,
      visitedCountryPoints: visitedCountryPoints,
      outlineFor: outlineFor,
      groupPhotosByRegion: groupPhotosByRegion,
      pointInPolygon: pointInPolygon
    },
    PALETTE_TERRACOTTA: TERRACOTTA.slice(),
    CONST: CONST
  };

  var root = (typeof window !== 'undefined') ? window : globalThis;
  root.KRPressEngine = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
